import { Layers } from '@orgus/ui/icons';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ALL_PROJECTS_WORKSPACE_ID } from '@orgus/protocol';
import { Button, EmptyState, cn } from '@orgus/ui';
import { outOfScope } from '../../lib/scope.ts';
import type { ScopedRef } from '../../lib/scope.ts';
import { useOrgus } from '../../lib/store.ts';
import { useCurrentWorkspace, useWorkspaceChoice } from './WorkspaceSwitcher.tsx';

export interface OutOfScopeInfo {
  /** The project's name, or null when the item has no project. */
  projectName: string | null;
  workspaceName: string;
  /** Switch this browser to All projects. */
  showAll: () => void;
}

/**
 * A deep link (a notification, a shared URL) names something the chosen workspace hides:
 * which project it is in and how to see it. Null when it is in scope, or does not exist.
 */
export function useOutOfScope(ref: ScopedRef | null): OutOfScopeInfo | null {
  const { full, scope } = useOrgus(useShallow((s) => ({ full: s.full, scope: s.scope })));
  const ws = useCurrentWorkspace();
  const choose = useWorkspaceChoice((s) => s.choose);
  const key = ref ? JSON.stringify(ref) : null;
  return useMemo(() => {
    if (!ref) return null;
    const hit = outOfScope(full, scope, ref);
    if (!hit) return null;
    return {
      projectName: hit.projectId ? (full.projects[hit.projectId]?.name ?? hit.projectId) : null,
      workspaceName: ws?.name ?? 'this',
      showAll: () => choose(ALL_PROJECTS_WORKSPACE_ID),
    };
    // key stands for ref, which callers build inline.
  }, [key, full, scope, ws?.name, choose]);
}

/** What to say about an item the workspace hides, in one sentence. */
export function outOfScopeText(what: string, info: OutOfScopeInfo): string {
  if (what === 'project' && info.projectName) return `${info.projectName} is outside the ${info.workspaceName} workspace.`;
  return info.projectName
    ? `This ${what} is in ${info.projectName}, outside the ${info.workspaceName} workspace.`
    : `This ${what} has no project, so the ${info.workspaceName} workspace does not show it.`;
}

/** The empty state for a drawer or page whose item the workspace hides, with the way out. */
export function OutOfScopeState({ what, info, size = 'compact', className }: { what: string; info: OutOfScopeInfo; size?: 'compact' | 'page'; className?: string }) {
  return (
    <EmptyState
      size={size}
      className={className}
      icon={<Layers />}
      title={`This ${what} is outside ${info.workspaceName}`}
      description={`${outOfScopeText(what, info)} Show all projects to open it, or switch back to ${info.workspaceName} from the top bar later.`}
      action={
        <Button variant="primary" icon={<Layers />} onClick={info.showAll}>
          Show all projects
        </Button>
      }
    />
  );
}

/** A one-line notice above a page, for a deep link the workspace hides. */
export function OutOfScopeNotice({ what, info, className }: { what: string; info: OutOfScopeInfo; className?: string }) {
  return (
    <div role="status" className={cn('flex flex-wrap items-center gap-3 rounded-lg border border-accent-line bg-accent-soft px-4 py-3', className)}>
      <Layers className="size-4 shrink-0 text-fg-subtle" aria-hidden />
      <p className="min-w-0 flex-1 text-sm text-fg">{outOfScopeText(what, info)}</p>
      <Button size="sm" variant="primary" icon={<Layers />} onClick={info.showAll}>
        Show all projects
      </Button>
    </div>
  );
}
